import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getInstructorAnalytics,
  getInstructorCycleAnalytics,
  getInstructorQAAnalytics,
  getTestSessionConversation,
  getCycleSessionConversation,
  getConversationHistory,
} from '../services/api';
import '../styles/InstructorAnalytics.css';
import '../styles/StudentOverview.css';

// Shared with InstructorAnalytics' visual language (ia-grade-good / mid / low).
function gradeClass(score) {
  if (score == null) return '';
  if (score >= 80) return 'ia-grade-good';
  if (score >= 60) return 'ia-grade-mid';
  return 'ia-grade-low';
}

// Date and time.
function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return '';
  }
}

// Dollars, or nothing for threads that predate per-thread cost tracking.
function fmtCost(value) {
  if (value == null) return '';
  return `$${Number(value).toFixed(2)}`;
}

// Newest of a set of ISO timestamps (string compare is valid for ISO-8601).
function latestIso(...isos) {
  return isos.filter(Boolean).sort().slice(-1)[0] || null;
}

// Normalize llm_feedback fields, which may arrive as a string or a list.
function feedbackLines(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  return [String(value)];
}

// Renders a chat transcript's messages, or its loading / empty state.
function TranscriptMessages({ loading, messages }) {
  if (loading) return <p className="so-muted">Loading transcript…</p>;
  if (!messages || messages.length === 0) return <p className="so-muted">No messages in this thread.</p>;
  return messages.map((m, i) => {
    const role = m.is_instructor ? 'instructor' : m.is_ai ? 'ai' : 'student';
    return (
      <div key={m.id ?? i} className={`so-msg so-msg--${role}`}>
        <span className="so-msg-role">
          {role === 'instructor' ? '🎓 Instructor' : role === 'ai' ? '✦ PaperBuddy' : '👤 Student'}
          {m.timestamp && <span className="so-msg-time">{fmtDateTime(m.timestamp)}</span>}
        </span>
        <div className="so-msg-text">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.message || ''}</ReactMarkdown>
        </div>
      </div>
    );
  });
}

// Secondary "View full chat transcript" toggle, nested inside a test attempt
// (which already shows per-question detail above it).
function TranscriptToggle({ open, onToggle, loading, messages }) {
  return (
    <div className="so-transcript">
      <button type="button" className="so-transcript-toggle" onClick={onToggle}>
        {open ? '▾ Hide full chat transcript' : '▸ View full chat transcript'}
      </button>
      {open && (
        <div className="so-transcript-body">
          <TranscriptMessages loading={loading} messages={messages} />
        </div>
      )}
    </div>
  );
}

// The clickable summary row shared by every collapsible item.
function DropHead({ expanded, onToggle, children }) {
  return (
    <button type="button" className="so-drop-head" onClick={onToggle} aria-expanded={expanded}>
      <span className="so-drop-caret">{expanded ? '▾' : '▸'}</span>
      {children}
    </button>
  );
}

// One graded question inside a test attempt. Collapsed shows just Q# + grade;
// clicking it reveals the question text, the student's answer, and feedback.
function QResultItem({ q }) {
  const [open, setOpen] = useState(false);
  const grade = q.effective_numerical_grade ?? q.numerical_grade;
  const cat = q.effective_categorical_grade ?? q.categorical_grade;
  const fb = q.llm_feedback || {};
  const hasFeedback = feedbackLines(fb.strengths).length > 0
    || feedbackLines(fb.weaknesses).length > 0
    || feedbackLines(fb.improvements).length > 0;
  return (
    <div className={`so-q-item${open ? ' so-q-item--open' : ''}`}>
      <button type="button" className="so-q-head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="so-q-caret">{open ? '▾' : '▸'}</span>
        <span className="so-q-num">Q{q.question_number}</span>
        {grade != null && <span className={`ia-grade-badge ${gradeClass(grade)}`}>{grade}%</span>}
        {cat && <span className="so-cat">{cat}</span>}
        {q.graded_by_instructor && <span className="so-override">instructor-graded</span>}
      </button>
      {open && (
        <div className="so-q-detail">
          {q.question_text
            ? <p className="so-question"><span className="so-q-label">Question:</span> {q.question_text}</p>
            : <p className="so-question so-muted">Question text unavailable for this attempt.</p>}
          {q.student_response && (
            <p className="so-answer"><span className="so-answer-label">Answer:</span> {q.student_response}</p>
          )}
          {hasFeedback && (
            <div className="so-feedback">
              {feedbackLines(fb.strengths).length > 0 && (
                <p><span className="so-fb-good">Strengths:</span> {feedbackLines(fb.strengths).join(' ')}</p>
              )}
              {feedbackLines(fb.weaknesses).length > 0 && (
                <p><span className="so-fb-bad">Weaknesses:</span> {feedbackLines(fb.weaknesses).join(' ')}</p>
              )}
              {feedbackLines(fb.improvements).length > 0 && (
                <p><span className="so-fb-mid">To improve:</span> {feedbackLines(fb.improvements).join(' ')}</p>
              )}
            </div>
          )}
          {q.instructor_note && <p className="so-note">📝 {q.instructor_note}</p>}
        </div>
      )}
    </div>
  );
}

// ── Per-mode detail blocks (collapsed by default) ──────────────────────────────

function TestAttemptBlock({ attempt, getTranscript }) {
  const [expanded, setExpanded] = useState(false);
  const [txOpen, setTxOpen] = useState(false);
  const tx = getTranscript(`test:${attempt.session_id}`);
  const results = [...(attempt.question_results || [])].sort(
    (a, b) => (a.question_number || 0) - (b.question_number || 0)
  );
  return (
    <div className="so-drop so-drop--test">
      <DropHead expanded={expanded} onToggle={() => setExpanded(e => !e)}>
        <span className="ia-attempt-label">{attempt.plan_label || 'Test'}</span>
        {attempt.average_grade != null && (
          <span className={`ia-grade-badge ${gradeClass(attempt.average_grade)}`}>
            {Math.round(attempt.average_grade)}%
          </span>
        )}
        {attempt.ai_flags > 0 && <span className="so-flag-chip">⚑ {attempt.ai_flags}</span>}
        {attempt.total_hints_used > 0 && <span className="so-hint-chip">💡 {attempt.total_hints_used} hints</span>}
        <span className="ia-attempt-date">{fmtDateTime(attempt.completed_at)}</span>
      </DropHead>

      {expanded && (
        <div className="so-drop-body">
          <div className="so-q-list">
            {results.map(q => (
              <QResultItem key={q.result_id ?? q.question_number} q={q} />
            ))}
          </div>

          <TranscriptToggle
            open={txOpen}
            onToggle={() => { setTxOpen(o => !o); if (!txOpen) tx.load(() => getTestSessionConversation(attempt.session_id)); }}
            loading={tx.loading}
            messages={tx.messages}
          />
        </div>
      )}
    </div>
  );
}

function CycleAttemptBlock({ attempt, getTranscript }) {
  const [expanded, setExpanded] = useState(false);
  const tx = getTranscript(`cycle:${attempt.session_id}`);
  const pct = attempt.completion_pct != null ? Math.round(attempt.completion_pct) : null;
  const toggle = () => {
    setExpanded(e => !e);
    if (!expanded) tx.load(() => getCycleSessionConversation(attempt.session_id));
  };
  return (
    <div className="so-drop so-drop--cycle">
      <DropHead expanded={expanded} onToggle={toggle}>
        <span className="ia-attempt-label">{attempt.plan_label || 'Cycle run'}</span>
        <span className="ia-attempt-progress-chip">
          {attempt.cycles_completed}/{attempt.total_cycles} cycles{pct != null ? ` · ${pct}%` : ''}
        </span>
        <span className={`so-status so-status--${attempt.status === 'completed' ? 'done' : 'wip'}`}>
          {attempt.status === 'completed' ? 'Completed' : 'In progress'}
        </span>
        {attempt.ai_flags > 0 && <span className="so-flag-chip">⚑ {attempt.ai_flags}</span>}
        <span className="ia-attempt-date">{fmtDateTime(attempt.completed_at || attempt.updated_at)}</span>
      </DropHead>
      {expanded && (
        <div className="so-drop-body so-transcript-body">
          <TranscriptMessages loading={tx.loading} messages={tx.messages} />
        </div>
      )}
    </div>
  );
}

function QAThreadBlock({ thread, studentUserId, paperId, getTranscript }) {
  const [expanded, setExpanded] = useState(false);
  const tx = getTranscript(`qa:${thread.conversation_id}`);
  const toggle = () => {
    setExpanded(e => !e);
    if (!expanded) tx.load(() => getConversationHistory(studentUserId, paperId, thread.session_number, thread.chat_index));
  };
  return (
    <div className="so-drop so-drop--qa">
      <DropHead expanded={expanded} onToggle={toggle}>
        <span className="ia-attempt-label">{thread.title}</span>
        {thread.score != null && <span className="ia-qa-score-chip">score {thread.score}/100</span>}
        <span className="so-msg-count">{thread.student_msgs} student · {thread.ai_msgs} AI</span>
        {thread.flag_count > 0 && <span className="so-flag-chip">⚑ {thread.flag_count}</span>}
        {thread.cost_usd != null && (
          <span className="so-cost-chip" title="AI cost for this conversation">{fmtCost(thread.cost_usd)}</span>
        )}
        <span className="ia-attempt-date">{fmtDateTime(thread.last_activity)}</span>
      </DropHead>
      {expanded && (
        <div className="so-drop-body so-transcript-body">
          <TranscriptMessages loading={tx.loading} messages={tx.messages} />
        </div>
      )}
    </div>
  );
}

function Section({ title, count, tone, children }) {
  return (
    <div className={`so-section so-section--${tone || 'qa'}`}>
      <h4 className="so-section-title">
        <span className="so-section-dot" />
        {title}
        <span className="so-section-count">{count}</span>
      </h4>
      {count > 0 ? children : <p className="so-muted so-section-empty">Nothing in {title.toLowerCase()} yet.</p>}
    </div>
  );
}

// ── Selected-student consolidated detail ───────────────────────────────────────

function StudentDetail({ student, paperId, onBack }) {
  // Transcript cache shared across every block for this student. Keyed by a
  // stable id ("test:42", "qa:7", …); each entry tracks its own loading state.
  const [cache, setCache] = useState({});

  const getTranscript = useCallback((key) => {
    const entry = cache[key] || { loading: false, messages: null, loaded: false };
    const load = (fetcher) => {
      if (entry.loaded || entry.loading) return;
      setCache(c => ({ ...c, [key]: { ...entry, loading: true } }));
      fetcher()
        .then(data => {
          const messages = Array.isArray(data) ? data : (data?.messages || []);
          setCache(c => ({ ...c, [key]: { loading: false, messages, loaded: true } }));
        })
        .catch(() => setCache(c => ({ ...c, [key]: { loading: false, messages: [], loaded: true } })));
    };
    return { ...entry, load };
  }, [cache]);

  const test = student.test;
  const cycle = student.cycle;
  const qa = student.qa;
  const initials = (student.name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

  // Threads opened from an instructor marker are listed apart from free-form Q&A.
  const allThreads = qa?.threads || [];
  const markerThreads = allThreads.filter(t => t.annotation_id);
  const plainThreads = allThreads.filter(t => !t.annotation_id);

  return (
    <div className="so-detail ia-scroll">
      <button type="button" className="so-back-btn" onClick={onBack}>← All students</button>
      <div className="so-detail-head">
        <div className="so-detail-id">
          <span className="so-avatar">{initials}</span>
          <div>
            <h3 className="so-detail-name">{student.name}</h3>
            {student.email && <p className="so-detail-email">{student.email}</p>}
          </div>
        </div>
        <div className="so-detail-stats">
          <span className="so-stat so-stat--test"><b>{student.tests}</b> Tests</span>
          <span className="so-stat so-stat--cycle"><b>{student.cycles}</b> Cycles</span>
          <span className="so-stat so-stat--qa"><b>{student.threads}</b> Q&amp;A</span>
          {student.aiFlags > 0 && <span className="so-stat so-stat--flag"><b>⚑ {student.aiFlags}</b> AI Flags</span>}
          {student.paperSpend != null && (
            <span className="so-stat so-stat--spend" title="AI spend on this paper">
              <b>{fmtCost(student.paperSpend)}</b>
              {student.paperBudget != null && ` of ${fmtCost(student.paperBudget)}`}
            </span>
          )}
        </div>
      </div>

      <Section title="Tests" tone="test" count={student.tests}>
        {(test?.attempts || []).map(a => (
          <TestAttemptBlock key={a.session_id} attempt={a} getTranscript={getTranscript} />
        ))}
      </Section>

      <Section title="Cycles" tone="cycle" count={student.cycles}>
        {(cycle?.attempts || []).map(a => (
          <CycleAttemptBlock key={a.session_id} attempt={a} getTranscript={getTranscript} />
        ))}
      </Section>

      <Section title="Q&A" tone="qa" count={plainThreads.length}>
        {plainThreads.map(t => (
          <QAThreadBlock
            key={t.conversation_id}
            thread={t}
            studentUserId={student.user_id}
            paperId={paperId}
            getTranscript={getTranscript}
          />
        ))}
      </Section>

      <Section title="Annotations" tone="annotation" count={markerThreads.length}>
        {markerThreads.map(t => (
          <QAThreadBlock
            key={t.conversation_id}
            thread={t}
            studentUserId={student.user_id}
            paperId={paperId}
            getTranscript={getTranscript}
          />
        ))}
      </Section>
    </div>
  );
}

// ── Top-level panel ─────────────────────────────────────────────────────────────

export default function StudentOverview({ paper, onBack }) {
  const [testData, setTestData] = useState(null);
  const [cycleData, setCycleData] = useState(null);
  const [qaData, setQaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getInstructorAnalytics(paper.id).catch(() => null),
      getInstructorCycleAnalytics(paper.id).catch(() => null),
      getInstructorQAAnalytics(paper.id).catch(() => null),
    ]).then(([t, c, q]) => {
      if (cancelled) return;
      setTestData(t);
      setCycleData(c);
      setQaData(q);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [paper.id, refreshKey]);

  // Merge the three per-mode student lists into one roster keyed by user_id.
  const roster = useMemo(() => {
    const m = new Map();
    const ensure = (s) => {
      if (!m.has(s.user_id)) {
        m.set(s.user_id, {
          user_id: s.user_id,
          name: s.student_name,
          email: s.student_email,
          test: null, cycle: null, qa: null,
        });
      }
      return m.get(s.user_id);
    };
    (testData?.students || []).forEach(s => { ensure(s).test = s; });
    (cycleData?.students || []).forEach(s => { ensure(s).cycle = s; });
    (qaData?.students || []).forEach(s => { ensure(s).qa = s; });

    return [...m.values()].map(r => ({
      ...r,
      tests: r.test?.total_attempts || 0,
      cycles: r.cycle?.total_attempts || 0,
      threads: r.qa?.thread_count || 0,
      aiFlags: (r.test?.ai_flags || 0) + (r.cycle?.ai_flags || 0) + (r.qa?.ai_flag_count || 0),
      paperSpend: r.qa?.paper_spend_usd ?? null,
      paperBudget: r.qa?.paper_budget_usd ?? null,
      lastActivity: latestIso(r.test?.completed_at, r.cycle?.last_activity, r.qa?.last_activity),
    })).sort((a, b) => (b.lastActivity || '').localeCompare(a.lastActivity || ''));
  }, [testData, cycleData, qaData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter(r =>
      (r.name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q)
    );
  }, [roster, query]);

  const selected = roster.find(r => r.user_id === selectedId) || null;

  return (
    <div className="ia-panel">
      <div className="ia-header">
        <div className="ia-header-left">
          <div className="ia-header-title-row">
            <h3 className="ia-title so-title">Student overview</h3>
          </div>
          <span className="so-subtitle">All activities per student</span>
        </div>
        <span className="ia-paper-name">{paper.name}</span>
        <div className="ia-header-controls">
          {onBack && (
            <button type="button" className="ia-refresh-btn" title="Close" onClick={onBack}>
              ← Back
            </button>
          )}
          <button type="button" className="ia-refresh-btn" title="Refresh" onClick={() => setRefreshKey(k => k + 1)}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="ia-loading">Loading student activity…</div>
      ) : roster.length === 0 ? (
        <div className="ia-empty">No student activity for this paper yet.</div>
      ) : selected ? (
        <StudentDetail student={selected} paperId={paper.id} onBack={() => setSelectedId(null)} />
      ) : (
        <div className="so-roster ia-scroll">
          <input
            className="so-search"
            type="text"
            placeholder="Search students…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div className="so-roster-list">
            {filtered.map(r => (
              <button
                key={r.user_id}
                type="button"
                className="so-roster-item"
                onClick={() => setSelectedId(r.user_id)}
              >
                <span className="so-roster-name">{r.name}</span>
                <span className="so-roster-chips">
                  {r.tests > 0 && <span className="so-chip so-chip--test">{r.tests}T</span>}
                  {r.cycles > 0 && <span className="so-chip so-chip--cycle">{r.cycles}C</span>}
                  {r.threads > 0 && <span className="so-chip so-chip--qa">{r.threads}Q</span>}
                  {r.aiFlags > 0 && <span className="so-chip so-chip--flag">⚑{r.aiFlags}</span>}
                  <span className="so-roster-arrow">›</span>
                </span>
              </button>
            ))}
            {filtered.length === 0 && <p className="so-muted so-roster-empty">No students match.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
